import { forwardRef, CSSProperties } from 'react';

// icons yo
import {
  faCanadianMapleLeaf,
  faFacebookF,
  faGithub,
  faInstagram,
  faLinkedinIn,
  faPatreon,
  faPaypal,
  faSkype,
  faTiktok,
  faTwitch,
  faXTwitter,
  faWeixin,
  faWhatsapp,
  faYelp,
  faYoutube,
} from '@fortawesome/free-brands-svg-icons';
import {
  faBrowser,
  faEnvelope,
  faEnvelopeOpen,
  faEnvelopeOpenText,
  faExternalLink,
  faFax,
  faGlobe,
  faInbox,
  faInboxIn,
  faLaptop,
  faLaptopCode,
  faLeaf,
  faLeafHeart,
  faLeafMaple,
  faLeafOak,
  faLink,
  faLocation,
  faLocationArrow,
  faMailbox,
  faMap,
  faMapMarkedAlt,
  faMapMarkerAlt,
  faMapPin,
  faMobile,
  faMobileAndroid,
  faPaperclip,
  faPaperPlane,
  faPhone,
  faPhoneOffice,
  faPhoneRotary,
  faPrint,
  faTree,
  faTreeAlt,
  faTreeDecorated,
  faTreeLarge,
  faTrees,
  faVideo,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BlinqIconPath } from '@components/BlinqAssets';

// types
import { EmailSignatureIconName } from './types';
import { SizeProp } from '@fortawesome/fontawesome-svg-core';
interface IconProps {
  icon: EmailSignatureIconName;
  inlineStyle?: CSSProperties;
  size?: 'sm' | '1x' | 'lg';
}

const iconSizesDict: Record<string, string> = {
  sm: '0.875em',
  lg: '1.33em',
  '1x': '1em',
};
const EmailSignatureIcon = forwardRef<SVGSVGElement, IconProps>(
  ({ size = 'sm' as SizeProp, inlineStyle, icon }, forwardedRef) => {
    switch (icon) {
      case 'envelope':
        return <FontAwesomeIcon ref={forwardedRef} style={inlineStyle} size={size} icon={faEnvelope} />;
      case 'envelope-open':
        return (
          <FontAwesomeIcon
            ref={forwardedRef}
            style={inlineStyle}
            size={size as SizeProp}
            icon={faEnvelopeOpen}
          />
        );
      case 'envelope-open-text':
        return (
          <FontAwesomeIcon ref={forwardedRef} style={inlineStyle} size={size} icon={faEnvelopeOpenText} />
        );
      case 'external-link':
        return <FontAwesomeIcon ref={forwardedRef} style={inlineStyle} size={size} icon={faExternalLink} />;
      case 'fax':
        return <FontAwesomeIcon ref={forwardedRef} style={inlineStyle} size={size} icon={faFax} />;
      case 'globe':
        return <FontAwesomeIcon ref={forwardedRef} style={inlineStyle} size={size} icon={faGlobe} />;
      case 'inbox':
        return <FontAwesomeIcon ref={forwardedRef} style={inlineStyle} size={size} icon={faInbox} />;
      case 'inbox-in':
        return <FontAwesomeIcon ref={forwardedRef} style={inlineStyle} size={size} icon={faInboxIn} />;
      case 'laptop':
        return <FontAwesomeIcon ref={forwardedRef} style={inlineStyle} size={size} icon={faLaptop} />;
      case 'laptop-code':
        return <FontAwesomeIcon ref={forwardedRef} style={inlineStyle} size={size} icon={faLaptopCode} />;
      case 'link':
        return <FontAwesomeIcon ref={forwardedRef} style={inlineStyle} size={size} icon={faLink} />;
      case 'location':
        return <FontAwesomeIcon ref={forwardedRef} style={inlineStyle} size={size} icon={faLocation} />;
      case 'location-arrow':
        return <FontAwesomeIcon ref={forwardedRef} style={inlineStyle} size={size} icon={faLocationArrow} />;
      case 'mailbox':
        return <FontAwesomeIcon ref={forwardedRef} style={inlineStyle} size={size} icon={faMailbox} />;
      case 'map':
        return <FontAwesomeIcon ref={forwardedRef} style={inlineStyle} size={size} icon={faMap} />;
      case 'map-marked-alt':
        return <FontAwesomeIcon ref={forwardedRef} style={inlineStyle} size={size} icon={faMapMarkedAlt} />;
      case 'map-marker-alt':
        return <FontAwesomeIcon ref={forwardedRef} style={inlineStyle} size={size} icon={faMapMarkerAlt} />;
      case 'map-pin':
        return <FontAwesomeIcon ref={forwardedRef} style={inlineStyle} size={size} icon={faMapPin} />;
      case 'mobile':
        return <FontAwesomeIcon ref={forwardedRef} style={inlineStyle} size={size} icon={faMobile} />;
      case 'mobile-android':
        return <FontAwesomeIcon ref={forwardedRef} style={inlineStyle} size={size} icon={faMobileAndroid} />;
      case 'paperclip':
        return <FontAwesomeIcon ref={forwardedRef} style={inlineStyle} size={size} icon={faPaperclip} />;
      case 'paper-plane':
        return <FontAwesomeIcon ref={forwardedRef} style={inlineStyle} size={size} icon={faPaperPlane} />;
      case 'phone':
        return <FontAwesomeIcon ref={forwardedRef} style={inlineStyle} size={size} icon={faPhone} />;
      case 'phone-alt':
        return <FontAwesomeIcon ref={forwardedRef} style={inlineStyle} size={size} icon={faPhone} />;
      case 'phone-office':
        return <FontAwesomeIcon ref={forwardedRef} style={inlineStyle} size={size} icon={faPhoneOffice} />;
      case 'phone-rotary':
        return <FontAwesomeIcon ref={forwardedRef} style={inlineStyle} size={size} icon={faPhoneRotary} />;
      case 'print':
        return <FontAwesomeIcon ref={forwardedRef} style={inlineStyle} size={size} icon={faPrint} />;
      case 'video':
        return <FontAwesomeIcon ref={forwardedRef} style={inlineStyle} size={size} icon={faVideo} />;
      case 'skype':
        return <FontAwesomeIcon ref={forwardedRef} style={inlineStyle} size={size} icon={faSkype} />;
      case 'snapchat':
        return (
          <svg
            aria-hidden="true"
            focusable="false"
            data-prefix="fab"
            data-icon="snapchat-ghost"
            className="svg-inline--fa fa-snapchat-ghost fa-w-16 fa-1x "
            role="img"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 512 512"
            ref={forwardedRef}
            style={{ width: iconSizesDict[size], ...inlineStyle, verticalAlign: '-.125em' }}
          >
            <path
              fill="currentColor"
              d="M510.846 392.673c-5.211 12.157-27.239 21.089-67.36 27.318-2.064 2.786-3.775 14.686-6.507 23.956-1.625 5.566-5.623 8.869-12.128 8.869l-.297-.005c-9.395 0-19.203-4.323-38.852-4.323-26.521 0-35.662 6.043-56.254 20.588-21.832 15.438-42.771 28.764-74.027 27.399-31.646 2.334-58.025-16.908-72.871-27.404-20.714-14.643-29.828-20.582-56.241-20.582-18.864 0-30.736 4.72-38.852 4.72-8.073 0-11.213-4.922-12.422-9.04-2.703-9.189-4.404-21.263-6.523-24.13-20.679-3.209-67.31-11.344-68.498-32.15a10.627 10.627 0 0 1 8.877-11.069c69.583-11.455 100.924-82.901 102.227-85.934.074-.176.155-.344.237-.515 3.713-7.537 4.544-13.849 2.463-18.753-5.05-11.896-26.872-16.164-36.053-19.796-23.715-9.366-27.015-20.128-25.612-27.504 2.437-12.836 21.725-20.735 33.002-15.453 8.919 4.181 16.843 6.297 23.547 6.297 5.022 0 8.212-1.204 9.96-2.171-2.043-35.936-7.101-87.29 5.687-115.969C158.122 21.304 229.705 15.42 250.826 15.42c.944 0 9.141-.089 10.11-.089 52.148 0 102.254 26.78 126.723 81.643 12.777 28.65 7.749 79.792 5.695 116.009 1.582.872 4.357 1.942 8.599 2.139 6.397-.286 13.815-2.389 22.069-6.257 6.085-2.846 14.406-2.461 20.48.058l.029.01c9.476 3.385 15.439 10.215 15.589 17.87.184 9.747-8.522 18.165-25.878 25.018-2.118.835-4.694 1.655-7.434 2.525-9.797 3.106-24.6 7.805-28.616 17.271-2.079 4.904-1.256 11.211 2.46 18.748.087.168.166.342.239.515 1.301 3.03 32.615 74.46 102.23 85.934 6.427 1.058 11.163 7.877 7.725 15.859z"
            ></path>
          </svg>
        );
      case 'whatsapp':
        return <FontAwesomeIcon ref={forwardedRef} style={inlineStyle} size={size} icon={faWhatsapp} />;
      case 'browser':
        return <FontAwesomeIcon ref={forwardedRef} style={inlineStyle} size={size} icon={faBrowser} />;
      case 'calendly':
        return (
          <svg
            ref={forwardedRef}
            aria-hidden="true"
            focusable="false"
            data-icon="calendly"
            role="img"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 512 512"
            style={{ width: iconSizesDict[size], ...inlineStyle, verticalAlign: '-.125em' }}
          >
            <path
              fill="currentColor"
              d="M424 61.12h-63.04v-8.56a12.4 12.4 0 10-24.72 0v8.56H172.4v-8a12.4 12.4 0 00-24.8 0v8H84.72a40 40 0 00-40 40V422a40 40 0 0040 40H424a40 40 0 0040-40V101.12a40 40 0 00-40-40zm14.56 361.44a16 16 0 01-16 16H85.28a16 16 0 01-16-16V100.8a16 16 0 0116-16h62.32v29.36a12.4 12.4 0 0024.8 0V84.8H336v29.04a12.4 12.4 0 0024.72 0V84.8h61.84a16 16 0 0116 16zM318.32 320.8a4.4 4.4 0 001.28-3.12 4.64 4.64 0 00-4.56-4.56 4.56 4.56 0 00-3.2 1.36 68.96 68.96 0 01-44 15.52 64.88 64.88 0 01-66.88-62.56 64.88 64.88 0 0166.88-62.64 69.52 69.52 0 0143.52 15.12 4.48 4.48 0 003.2 1.36 4.56 4.56 0 004.56-4.56 4.8 4.8 0 00-1.28-3.12 79.12 79.12 0 00-49.68-17.28c-42.08 0-76.16 32-76.16 71.36s34.08 71.36 76.16 71.36a78.4 78.4 0 0050.08-17.76z"
            ></path>
          </svg>
        );
      case 'discord':
        return (
          <svg
            aria-hidden="true"
            focusable="false"
            data-prefix="fab"
            data-icon="discord"
            className="svg-inline--fa fa-discord fa-w-14 "
            role="img"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 448 512"
            ref={forwardedRef}
            style={{ width: iconSizesDict[size], ...inlineStyle, verticalAlign: '-.125em' }}
          >
            <path
              fill="currentColor"
              d="M297.216 243.2c0 15.616-11.52 28.416-26.112 28.416-14.336 0-26.112-12.8-26.112-28.416s11.52-28.416 26.112-28.416c14.592 0 26.112 12.8 26.112 28.416zm-119.552-28.416c-14.592 0-26.112 12.8-26.112 28.416s11.776 28.416 26.112 28.416c14.592 0 26.112-12.8 26.112-28.416.256-15.616-11.52-28.416-26.112-28.416zM448 52.736V512c-64.494-56.994-43.868-38.128-118.784-107.776l13.568 47.36H52.48C23.552 451.584 0 428.032 0 398.848V52.736C0 23.552 23.552 0 52.48 0h343.04C424.448 0 448 23.552 448 52.736zm-72.96 242.688c0-82.432-36.864-149.248-36.864-149.248-36.864-27.648-71.936-26.88-71.936-26.88l-3.584 4.096c43.52 13.312 63.744 32.512 63.744 32.512-60.811-33.329-132.244-33.335-191.232-7.424-9.472 4.352-15.104 7.424-15.104 7.424s21.248-20.224 67.328-33.536l-2.56-3.072s-35.072-.768-71.936 26.88c0 0-36.864 66.816-36.864 149.248 0 0 21.504 37.12 78.08 38.912 0 0 9.472-11.52 17.152-21.248-32.512-9.728-44.8-30.208-44.8-30.208 3.766 2.636 9.976 6.053 10.496 6.4 43.21 24.198 104.588 32.126 159.744 8.96 8.96-3.328 18.944-8.192 29.44-15.104 0 0-12.8 20.992-46.336 30.464 7.68 9.728 16.896 20.736 16.896 20.736 56.576-1.792 78.336-38.912 78.336-38.912z"
            ></path>
          </svg>
        );
      case 'facebook':
        return <FontAwesomeIcon ref={forwardedRef} style={inlineStyle} size={size} icon={faFacebookF} />;
      case 'github':
        return <FontAwesomeIcon ref={forwardedRef} style={inlineStyle} size={size} icon={faGithub} />;
      case 'instagram':
        return <FontAwesomeIcon ref={forwardedRef} style={inlineStyle} size={size} icon={faInstagram} />;
      case 'linkedin':
        return <FontAwesomeIcon ref={forwardedRef} style={inlineStyle} size={size} icon={faLinkedinIn} />;
      case 'paypal':
        return <FontAwesomeIcon ref={forwardedRef} style={inlineStyle} size={size} icon={faPaypal} />;
      case 'blinq':
        return (
          <svg
            ref={forwardedRef}
            style={{
              width: iconSizesDict[size],
              height: iconSizesDict[size],
              ...inlineStyle,
              verticalAlign: '-.125em',
            }}
            aria-hidden="true"
            focusable="false"
            data-icon="blinq"
            role="img"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 171 512"
          >
            <BlinqIconPath />
          </svg>
        );
      case 'tiktok':
        return <FontAwesomeIcon ref={forwardedRef} style={inlineStyle} size={size} icon={faTiktok} />;
      case 'twitch':
        return <FontAwesomeIcon ref={forwardedRef} style={inlineStyle} size={size} icon={faTwitch} />;
      case 'twitter':
        return <FontAwesomeIcon ref={forwardedRef} style={inlineStyle} size={size} icon={faXTwitter} />;
      case 'venmo':
        return (
          <svg
            ref={forwardedRef}
            aria-hidden="true"
            focusable="false"
            data-icon="venmo"
            role="img"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 512 512"
            style={{ width: iconSizesDict[size], ...inlineStyle, verticalAlign: '-.125em' }}
          >
            <path
              fill="currentColor"
              d="M432.356 47.091c15.17 25.284 22.123 51.2 22.123 84.07 0 104.928-89.442 240.83-162.133 336.592H126.42L60.05 69.847 205.431 55.94l35.082 283.18c32.869-53.412 73.323-137.798 73.323-195.002 0-31.29-5.373-52.78-13.906-70.48l132.425-26.548z"
            ></path>
          </svg>
        );
      case 'cashapp':
        return (
          <svg
            ref={forwardedRef}
            aria-hidden="true"
            focusable="false"
            data-icon="cashapp"
            role="img"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 512 512"
            style={{ width: iconSizesDict[size], ...inlineStyle, verticalAlign: '-.125em' }}
          >
            <path
              fill="currentColor"
              d="M339.761 190.4c4 4 10.641 4 14.4 0l20-20.8c4.241-4 4-11.2-.48-15.52a157.829 157.829 0 00-53.76-30.72l6.321-30.4c1.36-6.64-3.6-12.88-10.24-12.88h-38.72c-2.421.026-4.76.883-6.622 2.426a10.553 10.553 0 00-3.618 6.054l-5.6 27.04c-51.52 2.64-95.2 28.8-95.2 82.4 0 46.399 36.08 66.32 74.24 79.999 36.08 13.76 55.2 18.88 55.2 38.24 0 19.921-19.04 31.601-47.2 31.601-25.6 0-52.48-8.561-73.28-29.44a10.403 10.403 0 00-11.344-2.26 10.403 10.403 0 00-3.376 2.26l-21.6 21.6a10.879 10.879 0 000 15.36c16.8 16.559 38.08 28.559 62.336 35.2l-5.92 28.559c-1.36 6.641 3.52 12.8 10.16 12.881l38.8.32a10.563 10.563 0 0010.4-8.48l5.6-27.119c61.984-4.16 99.744-38.321 99.744-88.162 0-45.92-37.6-65.28-83.2-81.039-26.08-9.681-48.64-16.32-48.64-36.24 0-19.36 21.04-27.04 42.16-27.04 26.88 0 52.72 11.12 69.6 26.32l-.16-.16z"
            ></path>
          </svg>
        );
      case 'wechat':
        return <FontAwesomeIcon ref={forwardedRef} style={inlineStyle} size={size} icon={faWeixin} />;
      case 'yelp':
        return <FontAwesomeIcon ref={forwardedRef} style={inlineStyle} size={size} icon={faYelp} />;
      case 'youtube':
        return <FontAwesomeIcon ref={forwardedRef} style={inlineStyle} size={size} icon={faYoutube} />;
      case 'signal':
        return (
          <svg
            ref={forwardedRef}
            aria-hidden="true"
            focusable="false"
            data-icon="signal"
            role="img"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 512 512"
            style={{ width: iconSizesDict[size], ...inlineStyle, verticalAlign: '-.125em' }}
          >
            <path
              fill="currentColor"
              d="M194.351 7.794l5.606 23.182c-22.418 5.595-44.035 14.389-64.05 26.38l-12.41-20.385c22.017-13.19 46.035-23.182 70.854-29.177zm123.298 0l-5.606 23.182c22.418 5.595 44.036 14.389 64.051 26.38l12.41-20.385c-22.018-13.19-46.037-23.182-70.855-29.177zm-281.02 115.91c-13.21 21.982-23.219 45.964-29.223 70.744l23.218 5.596c5.604-22.383 14.411-43.966 26.42-63.95l-20.415-12.39zM23.819 256c0-11.59.8-23.182 2.802-34.772L3.002 217.63C-1 243.21-1 268.79 3.002 294.37l23.619-3.598c-2.002-11.59-2.802-23.182-2.802-34.772zm364.685 219.03l-12.41-20.385c-20.015 11.99-41.633 21.183-64.451 26.78l5.605 23.181c25.22-6.396 49.238-16.387 71.256-29.576zM488.18 256c0 11.59-.8 23.182-2.801 34.772l23.618 3.598c4.003-25.58 4.003-51.16 0-76.74l-23.618 3.598c2 11.59 2.8 23.182 2.8 34.772zm16.414 61.552l-23.218-5.596c-5.606 22.782-14.412 44.366-26.421 64.35l20.415 12.391c13.212-22.384 23.218-46.364 29.224-71.145zm-213.768 167.87c-23.218 3.597-46.436 3.597-69.654 0l-3.603 23.58c25.62 3.997 51.24 3.997 76.86 0l-3.603-23.58zm152.12-91.93c-14.012 18.786-30.425 35.173-49.24 49.162l14.412 19.185c20.816-15.187 38.83-33.574 54.443-53.957l-19.616-14.39zm-49.24-324.146c18.815 13.989 35.228 30.376 49.24 49.161l19.215-14.388c-15.213-20.784-33.627-38.77-54.043-53.958l-14.412 19.185zM69.054 118.507c14.011-18.785 30.424-35.172 49.238-49.161l-14.41-19.185C83.064 65.349 65.05 83.735 49.838 104.119l19.215 14.388zm406.317 5.196l-20.415 12.39c12.009 19.985 21.215 41.568 26.82 64.351l23.22-5.596c-6.407-25.18-16.413-49.162-29.625-71.145zM221.173 26.58c23.218-3.597 46.436-3.597 69.654 0l3.603-23.581C268.81-1 243.19-1 217.57 2.998l3.603 23.581zM81.463 468.233l-49.638 11.593 11.609-49.562-23.618-5.596-11.61 49.562c-3.202 12.79 4.804 25.979 18.014 28.778 3.603.798 7.206.798 10.809 0l49.639-11.192-5.204-23.583zM25.02 403.484l23.218 5.596 8.006-34.372c-11.61-19.585-20.416-40.768-26.02-62.752l-23.219 5.596c5.205 21.183 13.21 41.567 23.619 60.752l-5.604 25.18zm112.087 51.96l-34.427 7.994 5.605 23.182 25.22-5.996c19.214 10.392 39.63 18.386 60.846 23.582l5.606-23.182c-22.018-5.196-43.235-13.989-62.85-25.58zM256 48.163C141.11 48.162 47.837 141.29 47.837 256c0 39.17 11.21 77.54 32.025 110.313l-20.015 85.535 85.266-19.985c97.277 61.151 225.776 31.975 287.024-65.15 61.249-97.124 32.025-225.423-65.251-286.576C333.66 59.354 295.23 48.162 256 48.162z"
            ></path>
          </svg>
        );
      case 'patreon':
        return <FontAwesomeIcon ref={forwardedRef} style={inlineStyle} size={size} icon={faPatreon} />;
      case 'leaf':
        return <FontAwesomeIcon ref={forwardedRef} style={inlineStyle} size={size} icon={faLeaf} />;
      case 'leaf-heart':
        return <FontAwesomeIcon ref={forwardedRef} style={inlineStyle} size={size} icon={faLeafHeart} />;
      case 'leaf-maple':
        return <FontAwesomeIcon ref={forwardedRef} style={inlineStyle} size={size} icon={faLeafMaple} />;
      case 'leaf-oak':
        return <FontAwesomeIcon ref={forwardedRef} style={inlineStyle} size={size} icon={faLeafOak} />;
      case 'canadian-maple-leaf':
        return (
          <FontAwesomeIcon ref={forwardedRef} style={inlineStyle} size={size} icon={faCanadianMapleLeaf} />
        );
      case 'tree':
        return <FontAwesomeIcon ref={forwardedRef} style={inlineStyle} size={size} icon={faTree} />;
      case 'trees':
        return <FontAwesomeIcon ref={forwardedRef} style={inlineStyle} size={size} icon={faTrees} />;
      case 'tree-alt':
        return <FontAwesomeIcon ref={forwardedRef} style={inlineStyle} size={size} icon={faTreeAlt} />;
      case 'tree-large':
        return <FontAwesomeIcon ref={forwardedRef} style={inlineStyle} size={size} icon={faTreeLarge} />;
      case 'tree-decorated':
        return <FontAwesomeIcon ref={forwardedRef} style={inlineStyle} size={size} icon={faTreeDecorated} />;
      default:
        return <></>;
    }
  }
);

EmailSignatureIcon.displayName = 'EmailSignatureIcon';

export default EmailSignatureIcon;
